import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import toast from "react-hot-toast"
import { getPracticePaper } from "../../../api/studentPracticePapers"
import { ASSESSMENT_COMPLETE } from "../../../api/studentNotifications"
import { getLearnerApiErrorMessage } from "../../../auth/learner/errors"
import LearnerAuthBootScreen from "../../../auth/learner/LearnerAuthBootScreen"
import { STUDENT_PRACTICE_PAPERS_PATH } from "../../../component/student/config/studentNavItems"
import useLearnerNotifications from "../../../hooks/useLearnerNotifications"
import AnalyticsView from "./components/AnalyticsView"
import "./components/AnalyticsView.style.scss"
import type { PaperSession } from "./types"

const paperIdFromNotification = (
  data: Record<string, unknown> | null | undefined
) => {
  const raw = data?.paper_id
  return typeof raw === "string" ? raw.trim() : ""
}

const PracticePaperAnalyticPage = () => {
  const { paperId = "" } = useParams()
  const navigate = useNavigate()
  const { notifications } = useLearnerNotifications()
  const [session, setSession] = useState<PaperSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const seenNotifications = useRef(new Set<string>())
  const hasSession = useRef(false)

  const load = useCallback(async () => {
    if (!paperId) {
      setError("Practice paper not found")
      setLoading(false)
      return
    }
    try {
      const res = await getPracticePaper(paperId)
      if (!res.data?.success || !res.data.data) {
        throw new Error(res.data?.message || "Could not load this paper")
      }
      hasSession.current = true
      setSession(res.data.data as PaperSession)
      setError("")
    } catch (err) {
      if (!hasSession.current) {
        setError(getLearnerApiErrorMessage(err, "Could not load this paper"))
      } else {
        toast.error(getLearnerApiErrorMessage(err, "Could not refresh marks"))
      }
    } finally {
      setLoading(false)
    }
  }, [paperId])

  useEffect(() => {
    hasSession.current = false
    setSession(null)
    setError("")
    setLoading(true)
    void load()
  }, [load])

  useEffect(() => {
    if (!session) return
    const fresh = notifications.filter(
      (item) =>
        item.notification_type === ASSESSMENT_COMPLETE &&
        item.id &&
        !seenNotifications.current.has(item.id) &&
        paperIdFromNotification(item.data) === paperId
    )
    if (!fresh.length) return
    for (const item of fresh) seenNotifications.current.add(item.id)
    void load()
  }, [notifications, paperId, session, load])

  const close = () => navigate(STUDENT_PRACTICE_PAPERS_PATH)

  if (loading && !session) {
    return (
      <LearnerAuthBootScreen
        title="Opening analytics…"
        hint="Loading this paper"
      />
    )
  }

  if (!session) {
    return (
      <div className="analytics-view">
        <header className="analytics-view__header">
          <div className="analytics-view__title">
            <button type="button" onClick={close} aria-label="Close analytics">
              Back
            </button>
            <div>
              <h2>Analytics</h2>
            </div>
          </div>
        </header>
        <main className="analytics-view__main">
          <div className="analytics-view__marking">
            <h3>{error || "Could not load this paper"}</h3>
            <button type="button" onClick={() => void load()}>
              Try again
            </button>
          </div>
        </main>
      </div>
    )
  }

  return <AnalyticsView session={session} onClose={close} />
}

export default PracticePaperAnalyticPage
